// Apolo IDE — Live preview via esbuild-wasm (Sprint v11 E11).
//
// Loads esbuild-wasm lazily from CDN the first time someone asks for a
// preview, then memoizes a single instance. The `transformJsx` helper
// returns transformed JS that can be injected straight into an iframe.
//
// Source loader resolution:
//   - .jsx / .tsx → JSX with TS support
//   - .ts         → TS only
//   - .js / .mjs  → pass through (still run through esbuild for minify-safe)
//   - .html       → returned as-is for HTML preview path
//
// Iframe scaffold uses an import map pointing at esm.sh so React + ReactDOM
// resolve without bundling.

const ESBUILD_CDN_WASM = 'https://unpkg.com/esbuild-wasm@0.24.0/esbuild.wasm';
const ESBUILD_CDN_MOD = 'https://unpkg.com/esbuild-wasm@0.24.0/esm/browser.min.js';

let initPromise = null;
let esbuildRef = null;

async function loadEsbuild() {
  if (esbuildRef) return esbuildRef;
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const mod = await import(/* @vite-ignore */ ESBUILD_CDN_MOD);
    await mod.initialize({ wasmURL: ESBUILD_CDN_WASM });
    esbuildRef = mod;
    return mod;
  })();
  return initPromise;
}

function pickLoader(filename) {
  const lower = (filename || '').toLowerCase();
  if (lower.endsWith('.tsx')) return 'tsx';
  if (lower.endsWith('.ts')) return 'ts';
  if (lower.endsWith('.jsx')) return 'jsx';
  return 'js';
}

/**
 * Transform a source snippet via esbuild. Throws on syntax error so the
 * caller can surface it in the preview pane.
 *
 * @param {string} source
 * @param {string} filename
 * @returns {Promise<string>}
 */
export async function transformJsx(source, filename) {
  if (!source) return '';
  const esbuild = await loadEsbuild();
  const loader = pickLoader(filename);
  const out = await esbuild.transform(source, {
    loader,
    jsx: 'automatic',
    jsxImportSource: 'react',
    target: 'es2022',
    format: 'esm',
    sourcemap: false,
  });
  return out.code;
}

/**
 * Build the iframe `srcdoc` string for a React component preview.
 * The transformed code MUST export default a component.
 *
 * @param {string} transformedJs  ESM output of esbuild.transform
 * @param {string} filename
 * @returns {string}
 */
export function buildReactSrcdoc(transformedJs, filename) {
  const isJsx = /\.(jsx|tsx)$/i.test(filename || '');
  const escaped = transformedJs
    .replace(/<\/script/g, '<\\/script');
  return `<!doctype html>
<html lang="pt-br">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Apolo Live Preview</title>
<style>
  :root { color-scheme: light dark; }
  body { font-family: system-ui, sans-serif; margin: 1rem; }
  #__apolo_error { white-space: pre-wrap; color: crimson; font-family: ui-monospace, Menlo, monospace; }
</style>
<script type="importmap">
{
  "imports": {
    "react": "https://esm.sh/react@18.3.1",
    "react-dom": "https://esm.sh/react-dom@18.3.1",
    "react-dom/client": "https://esm.sh/react-dom@18.3.1/client",
    "react/jsx-runtime": "https://esm.sh/react@18.3.1/jsx-runtime",
    "react/jsx-dev-runtime": "https://esm.sh/react@18.3.1/jsx-dev-runtime"
  }
}
</script>
</head>
<body>
<div id="root"></div>
<div id="__apolo_error" role="alert" aria-live="polite"></div>
<script type="module">
window.addEventListener('error', (e) => {
  document.getElementById('__apolo_error').textContent = e?.message || String(e);
});
window.addEventListener('unhandledrejection', (e) => {
  document.getElementById('__apolo_error').textContent = e?.reason?.message || String(e?.reason);
});
try {
  const mod = await import('data:text/javascript;base64,${btoa(unescape(encodeURIComponent(escaped)))}');
  const Component = mod.default;
  if (!Component) {
    document.getElementById('__apolo_error').textContent = '${isJsx ? 'O arquivo precisa exportar default um componente React.' : 'O arquivo precisa exportar default algo renderizável.'}';
  } else {
    const React = await import('react');
    const { createRoot } = await import('react-dom/client');
    createRoot(document.getElementById('root')).render(React.createElement(Component));
  }
} catch (err) {
  document.getElementById('__apolo_error').textContent = err?.message || String(err);
}
</script>
</body>
</html>`;
}

/**
 * Build a srcdoc for an HTML file — drop the source straight in. Useful for
 * static landing pages or sandboxed widgets. CSP is conservative.
 */
export function buildHtmlSrcdoc(source) {
  return source || '<!doctype html><meta charset="utf-8" /><p>Vazio</p>';
}
